import { useState } from "react";
import { FadeUp } from "@/components/motion";
import { Plus, Minus } from "lucide-react";

export const FAQS = [
  {
    q: "What is the CV Career Brand Score, and is it really free?",
    a: "It's a free, AI-supported assessment of how your CV currently positions you. Upload or paste your CV and you'll see a score across five areas — career positioning, achievements and evidence, clarity and readability, ATS keywords and relevance, and formatting and structure — with no payment or sign-up required. You can then request the full report by email.",
  },
  {
    q: "What does \u201Cprofessional positioning\u201D actually mean?",
    a: "It's the deliberate alignment of your target roles, relevant experience, skills, value and evidence of impact. Instead of describing where you've been, your CV and LinkedIn build a credible, substantiated case for where you're going.",
  },
  {
    q: "How is this different from a standard CV-writing service?",
    a: "Conventional CV writing improves the wording. We start with strategy: defining your target roles, aligning your experience to them, and substantiating the case with evidence — then translating that positioning across both your CV and LinkedIn profile.",
  },
  {
    q: "What's the process once I enquire?",
    a: "George replies within one business day. You talk about your goals and what you need. We then explain the scope of the work and give you a clear quote before you decide. Sending an enquiry does not commit you to anything.",
  },
  {
    q: "How long does it take?",
    a: "It depends on the scope we agree together. Most career support work is completed within one to two weeks. We confirm the timeframe with your quote, before any work begins.",
  },
  {
    q: "Are revisions included?",
    a: "Yes. Revision rounds are agreed as part of your quote. The goal is documents you are genuinely confident sending.",
  },
  {
    q: "How much does it cost?",
    a: "Pricing depends on what you need, so we do not publish fixed prices. Contact us to discuss your needs. We will explain the scope and provide a clear quote before you decide. Payments are processed securely in Australian dollars.",
  },
  {
    q: "Who are the services suitable for?",
    a: "People looking for a job, changing careers, returning to work, graduating, moving to the Australian market, or exploring a small business or self employment. Many of our clients speak English as a second language and we keep everything in plain language.",
  },
  {
    q: "Do you use AI?",
    a: "AI supports the assessment and analysis, but George reviews and shapes every recommendation in paid work. You remain the source of truth about your experience — nothing is invented or exaggerated.",
  },
  {
    q: "Will you exaggerate my experience to make me look better?",
    a: "No. Strong positioning is evidence-led. We work with your real achievements, outcomes and scope — framed clearly and aligned to your target roles. If a claim can't be substantiated, it doesn't go in.",
  },
];

export const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="faqs" id="faqs" className="relative bg-[#141414] border-y border-[#27272A] py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        <FadeUp className="lg:col-span-4">
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">FAQs</p>
          <h2 className="font-display font-bold tracking-tighter leading-[0.95] text-4xl md:text-5xl text-[#F8F8F5]">
            Practical questions, <span className="font-serif-accent font-light">answered.</span>
          </h2>
        </FadeUp>
        <div className="lg:col-span-8">
          <div className="divide-y divide-[#27272A] border-t border-[#27272A]">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <FadeUp key={i} delay={Math.min(i * 0.03, 0.2)}>
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                  >
                    <h3 className={`font-display text-lg md:text-xl font-bold tracking-tight transition-colors ${isOpen ? "text-[#DFFF00]" : "text-[#F8F8F5] group-hover:text-[#DFFF00]"}`}>
                      {f.q}
                    </h3>
                    <span className="mt-1 shrink-0 text-[#52525B] group-hover:text-[#DFFF00] transition-colors">
                      {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    </span>
                  </button>
                  {isOpen && (
                    <p data-testid={`faq-answer-${i}`} className="pb-7 -mt-1 font-body text-base text-[#A1A1AA] leading-relaxed max-w-2xl">
                      {f.a}
                    </p>
                  )}
                </FadeUp>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
